/**
 * Forge's Tooltip (`@forge/common`) centers itself under/over its anchor but never
 * clamps to the viewport, and never wraps long, space-less values (ids/names with
 * underscores) - so the Integration page's header Customer/Site selects, whose
 * value can be an arbitrarily long org/site name, can render their truncated-value
 * tooltip partially or fully off-screen. This watches only those two anchors (and
 * their open dropdown lists, which Forge portals separately) and, when their
 * tooltip appears, forces it to wrap and nudges it back on-screen - regardless of
 * how long the customer/site name grows in the future.
 *
 * The tooltip's inline position isn't only computed once: Forge recalculates it
 * whenever the dropdown list opens (it resize-observes the whole page), so the fix
 * is re-applied for as long as that tooltip instance stays mounted, not just once.
 */
const TOOLTIP_SELECTOR = '[data-testid="tooltip"]';
// The closed selects, plus their dropdown lists (`<ul id="{selectId}-dropdown">`),
// which Forge portals to document.body separately from the select itself.
const SCOPED_ANCHOR_SELECTOR =
  '[aria-label="select-customer"], [aria-label="select-site"], #select-customer-dropdown, #select-site-header-dropdown';
const VIEWPORT_MARGIN = 8;

function applyWrapping(tooltip: HTMLElement): void {
  tooltip.style.overflowWrap = 'anywhere';
  tooltip.style.wordBreak = 'break-word';
  tooltip.style.whiteSpace = 'normal';
}

function clampToViewport(tooltip: HTMLElement): void {
  const { style } = tooltip;
  const rect = tooltip.getBoundingClientRect();

  const overflowRight = rect.right - (window.innerWidth - VIEWPORT_MARGIN);
  if (overflowRight > 0) {
    if (style.left) {
      style.left = `${parseFloat(style.left) - overflowRight}px`;
    } else if (style.right) {
      style.right = `${parseFloat(style.right) + overflowRight}px`;
    }
  }

  const overflowLeft = VIEWPORT_MARGIN - rect.left;
  if (overflowLeft > 0) {
    if (style.left) {
      style.left = `${parseFloat(style.left) + overflowLeft}px`;
    } else if (style.right) {
      style.right = `${parseFloat(style.right) - overflowLeft}px`;
    }
  }

  const overflowBottom = rect.bottom - (window.innerHeight - VIEWPORT_MARGIN);
  if (overflowBottom > 0) {
    if (style.bottom) {
      style.bottom = `${parseFloat(style.bottom) + overflowBottom}px`;
    } else if (style.top) {
      style.top = `${parseFloat(style.top) - overflowBottom}px`;
    }
  }

  const overflowTop = VIEWPORT_MARGIN - rect.top;
  if (overflowTop > 0) {
    if (style.top) {
      style.top = `${parseFloat(style.top) + overflowTop}px`;
    } else if (style.bottom) {
      style.bottom = `${parseFloat(style.bottom) - overflowTop}px`;
    }
  }
}

function applyFix(tooltip: HTMLElement): void {
  applyWrapping(tooltip);
  clampToViewport(tooltip);
}

let activeObserver: MutationObserver | null = null;

function stopWatching(): void {
  activeObserver?.disconnect();
  activeObserver = null;
}

// Keep re-applying the fix every time Forge recomputes this tooltip's inline
// position (e.g. once the dropdown list finishes opening), until it's removed.
function watchTooltip(tooltip: HTMLElement): void {
  stopWatching();
  const observer = new MutationObserver(() => applyFix(tooltip));
  observer.observe(tooltip, { attributes: true, attributeFilter: ['style'] });
  activeObserver = observer;
}

function fixVisibleTooltip(): void {
  // Double rAF: wait for the library's own post-mount position calculation (and
  // the resulting layout) to finish before measuring/adjusting.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const tooltip = document.querySelector<HTMLElement>(TOOLTIP_SELECTOR);
    if (!tooltip) {
      return;
    }
    applyFix(tooltip);
    watchTooltip(tooltip);
  }));
}

function handleAnchorInteraction(event: Event): void {
  if ((event.target as HTMLElement | null)?.closest(SCOPED_ANCHOR_SELECTOR)) {
    fixVisibleTooltip();
  }
}

function handleAnchorLeave(event: Event): void {
  const target = event.target as HTMLElement | null;
  const related = (event as MouseEvent | FocusEvent).relatedTarget as HTMLElement | null;
  if (target?.closest(SCOPED_ANCHOR_SELECTOR) && !related?.closest?.(SCOPED_ANCHOR_SELECTOR)) {
    stopWatching();
  }
}

/** Only reacts to hover/focus on the Customer/Site selects and their dropdown lists; no other tooltip in the app is affected. */
export function initTooltipViewportClamp(): void {
  document.addEventListener('mouseover', handleAnchorInteraction, true);
  document.addEventListener('focusin', handleAnchorInteraction, true);
  document.addEventListener('mouseout', handleAnchorLeave, true);
  document.addEventListener('focusout', handleAnchorLeave, true);
}
